import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { RoutesPaths } from "../Router/constant";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { logoutAPI } from "../Services/authService";
import { Messageslist, MessageslistAdmin } from "../Services/messagesService";
import {
  FiBarChart2,
  FiBox,
  FiGrid,
  FiLogOut,
  FiMail,
  FiMessageSquare,
  FiSettings,
  FiUser,
  FiUserCheck,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { BiSupport } from "react-icons/bi";
import { MdOutlineHandshake, MdOutlineWarehouse } from "react-icons/md";

const MESSAGE_BADGE_PAGE = 1;
const MESSAGE_BADGE_LIMIT = 1000;
const ADMIN_READ_ROOMS_STORAGE_KEY = "adminMessagesReadRooms";
const PARTNER_READ_ROOMS_STORAGE_KEY = "partnerMessagesReadRooms";
const ADMIN_MESSAGE_TYPES = ["user", "partner"];

function SidebarSkeleton() {
  return (
    <div>
      <div className="skel skel-sideBar" />
      <div className="skel skel-sideBar" />
      <div className="skel skel-sideBar" />
      <div className="skel skel-sideBar" />
      <div className="skel skel-sideBar" />
    </div>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [IsSignOutModel, setIsSignOutModel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [role, setUserRole] = useState(null);
  const [sideLoader, setSideLoader] = useState(true);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const storedRole = localStorage.getItem("userRole");
      setUserRole(storedRole);
      setSideLoader(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const fetchMessageUnreadCount = useCallback(async () => {
    if (!role) return;

    try {
      const responses =
        role === "admin"
          ? await Promise.all(
              ADMIN_MESSAGE_TYPES.map((type) =>
                MessageslistAdmin(
                  type,
                  MESSAGE_BADGE_PAGE,
                  MESSAGE_BADGE_LIMIT,
                ),
              ),
            )
          : [await Messageslist(MESSAGE_BADGE_PAGE, MESSAGE_BADGE_LIMIT)];

      const storageKey =
        role === "admin"
          ? ADMIN_READ_ROOMS_STORAGE_KEY
          : PARTNER_READ_ROOMS_STORAGE_KEY;
      const readRooms = getReadRooms(storageKey);
      const unreadCount = responses
        .flatMap((response) => getRoomsData(response))
        .reduce(
          (total, room) =>
            total + (isRoomRead(room, readRooms) ? 0 : getUnreadCount(room)),
          0,
        );

      setMessageUnreadCount(unreadCount);
    } catch (error) {
      console.error("Load message unread count failed:", error);
      setMessageUnreadCount(0);
    }
  }, [role]);

  useEffect(() => {
    fetchMessageUnreadCount();
  }, [fetchMessageUnreadCount, location.pathname]);

  useEffect(() => {
    window.addEventListener("messageRoomsRead", fetchMessageUnreadCount);
    window.addEventListener("storage", fetchMessageUnreadCount);

    return () => {
      window.removeEventListener("messageRoomsRead", fetchMessageUnreadCount);
      window.removeEventListener("storage", fetchMessageUnreadCount);
    };
  }, [fetchMessageUnreadCount]);

  const [loggingOut, setLoggingOut] = useState(false);

  const signOutModel = () => setIsSignOutModel(true);
  const signOutModelClose = () => {
    // Keep the modal open while the logout request is in flight.
    if (!loggingOut) setIsSignOutModel(false);
  };

  const signOutSectionClose = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      const res = await logoutAPI();
      if (res.data?.data) {
        toast.success("Logged out successfully");

        localStorage.removeItem("token");
        localStorage.removeItem("userRole");
        setIsSignOutModel(false);
        navigate(RoutesPaths.SIGNIN);
      } else {
        toast.error(res.data?.message || "Logout failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Logout failed");
    } finally {
      setLoggingOut(false);
    }
  };

  const menuItems = useMemo(() => {
    if (!role) return [];

    return [
      {
        path: RoutesPaths.DASHBOARD,
        label: "Dashboard",
        Icon: FiGrid,
        icon:
          role === "partner"
            ? "/assets/img/dashboardPartner.svg"
            : "/assets/img/dashboard.svg",
      },

      ...(role === "partner"
        ? [
            {
              path: RoutesPaths.FREIGHTAGREEMENT,
              label: "Vendor contract",
              Icon: MdOutlineHandshake,
              icon: "/assets/img/partnerPartner.svg",
            },
          ]
        : []),

      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.USERMANAGEMENT,
              label: "User management",
              Icon: FiUser,
              icon: "/assets/img/document.svg",
            },
          ]
        : []),

      {
        path: RoutesPaths.BOOKINGMANAGEMENT,
        label: "Booking management",
        Icon: FiBox,
        icon:
          role === "partner"
            ? "/assets/img/office-documentsPartner.svg"
            : "/assets/img/office-documents.svg",
      },
      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.SUBSCRIBERMANAGEMENT,
              label: "Newsletter management",
              Icon: FiMail,
              icon: "/assets/img/subscriber.svg",
            },
          ]
        : []),

      // {
      //   path: RoutesPaths.PAYMENT,
      //   label: "Payment & transactions",
      //   icon:
      //     role === "partner"
      //       ? "/assets/img/rupee-circlePartner.svg"
      //       : "/assets/img/rupee-circle.svg",
      // },

      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.CONTACTSUPPORT,
              label: "Contact support",
              Icon: BiSupport,
              icon: "/assets/img/board.svg",
            },
          ]
        : []),

      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.SURVEY,
              label: "Survey",
              Icon: FiBarChart2,
              icon: "/assets/img/survey.svg",
            },
          ]
        : []),

      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.PARTNERS,
              label: "Vendors",
              Icon: FiUsers,
              icon: "/assets/img/partner-side.svg",
            },
          ]
        : []),
      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.WAREHOUSE,
              label: "Warehouse",
              Icon: MdOutlineWarehouse,
              icon: "/assets/img/office-documents.svg",
            },
          ]
        : []),
      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.ADMINMESSAGES,
              label: "Messages",
              Icon: FiMessageSquare,
              icon: "/assets/img/message.svg",
            },
          ]
        : []),

      ...(role === "partner"
        ? [
            {
              path: RoutesPaths.PARTNERMESSAGES,
              label: "Messages",
              Icon: FiMessageSquare,
              icon: "/assets/img/messageWhite.svg",
            },
          ]
        : []),
      ...(role === "admin"
        ? [
            {
              path: RoutesPaths.ADMINMANAGEMENT,
              label: "Admin management",
              Icon: FiUserCheck,
              icon: "/assets/img/partner-side.svg",
            },
          ]
        : []),

      // {
      //   path: RoutesPaths.PROFILE,
      //   label: "Settings",
      //   Icon: FiSettings,
      //   icon:
      //     role === "partner"
      //       ? "/assets/img/filterPartner.svg"
      //       : "/assets/img/filter.svg",
      // },
    ];
  }, [role]);

  const filteredItems = menuItems.filter((item) =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <>
      <div
        className={
          role === "admin" ? "adminSideNav" : role === "partner" ? "partnerSideNav" : ""
        }
      >
        <NavLink to={RoutesPaths.DASHBOARD} className="logoSidebar">
          {role === "partner" ? (
            <img
              src="/assets/img/quimeraLogoPartner.svg"
              alt="Logo"
              className=""
            />
          ) : (
            <img src="/assets/img/quimeraLogo.svg" alt="Logo" />
          )}
        </NavLink>

        {/* <div
          className={`searchSideBar ${role === "partner" ? "searchSideBarPartner" : ""
            }`}
        >
          <img
            src={
              role === "partner"
                ? "/assets/img/searchPartner.svg"
                : "/assets/img/search.svg"
            }
            alt="Search"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div> */}

        {/* ✅ SKELETON FIRST */}
        {sideLoader ? (
          <div className="sideBarSectionScroll">
            <SidebarSkeleton />
          </div>
        ) : (
          <div className="sideBarSectionScroll">
            {filteredItems.map(({ path, label, icon, Icon }) => {
              const active = location.pathname.startsWith(path);
              const isMessageItem =
                path === RoutesPaths.ADMINMESSAGES ||
                path === RoutesPaths.PARTNERMESSAGES;

              return (
                <NavLink
                  key={path}
                  to={path}
                  className={`sideBarSection ${
                    active ? "sideBarSectionActive" : ""
                  } ${role === "partner" ? "partnerSidebar" : ""}`}
                >
                  {Icon ? (
                    <Icon className="sideBarIcon" aria-hidden="true" />
                  ) : (
                    <img src={icon} alt={label} />
                  )}
                  <h6
                    className={`m-0 ${role === "partner" ? "partnerSidebarText" : ""}`}
                  >
                    {label}
                  </h6>
                  {isMessageItem && messageUnreadCount > 0 && (
                    <span className="sideBarUnreadBadge">
                      {messageUnreadCount}
                    </span>
                  )}
                </NavLink>
              );
            })}

            <div
              className={
                role === "partner" ? "sideBarSectionPartner" : "sideBarSection"
              }
              onClick={signOutModel}
            >
              {role === "partner" ? (
                <FiLogOut className="sideBarIcon" aria-hidden="true" />
              ) : (
                <FiLogOut className="sideBarIcon" aria-hidden="true" />
              )}
              <h6 className="m-0">Logout</h6>
            </div>

            {role !== "partner" && (
              <img
                src="/assets/img/sidebarShip.png"
                alt=""
                className="sideBarIllustration"
              />
            )}
          </div>
        )}
      </div>

      {/* LOGOUT MODAL */}
      <Modal
        centered
        show={IsSignOutModel}
        onHide={signOutModelClose}
        backdrop={loggingOut ? "static" : true}
        keyboard={!loggingOut}
        contentClassName="qlo-modal"
        aria-labelledby="logout-modal-title"
      >
        <div className="qlo-body">
          <button
            type="button"
            className="qlo-close"
            onClick={signOutModelClose}
            disabled={loggingOut}
            aria-label="Close"
          >
            <FiX />
          </button>

          <div className="qlo-icon" aria-hidden="true">
            <span>
              <FiLogOut />
            </span>
          </div>

          <h3 id="logout-modal-title" className="qlo-title">
            Log out of your account?
          </h3>
          <p className="qlo-text">
            You&apos;ll need to sign in again to access the{" "}
            {role === "partner" ? "vendor portal" : "admin panel"}.
          </p>

          <div className="qlo-actions">
            <button
              type="button"
              className="qlo-btn qlo-btn-cancel"
              onClick={signOutModelClose}
              disabled={loggingOut}
            >
              Cancel
            </button>
            <button
              type="button"
              className="qlo-btn qlo-btn-logout"
              onClick={signOutSectionClose}
              disabled={loggingOut}
              aria-busy={loggingOut}
            >
              {loggingOut ? (
                <>
                  <span className="qo-spinner" aria-hidden="true" /> Logging out...
                </>
              ) : (
                <>
                  <FiLogOut aria-hidden="true" /> Logout
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function getRoomsData(response) {
  const data = response?.data?.data ?? response?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rooms)) return data.rooms;
  if (Array.isArray(data?.data?.rooms)) return data.data.rooms;

  return [];
}

function getUnreadCount(room) {
  const unreadCount = Number(room?.unreadCount || 0);

  return Number.isFinite(unreadCount) && unreadCount > 0 ? unreadCount : 0;
}

function getReadRooms(storageKey) {
  if (typeof window === "undefined") return {};

  try {
    const savedRooms = JSON.parse(localStorage.getItem(storageKey) || "{}");

    return savedRooms && typeof savedRooms === "object" ? savedRooms : {};
  } catch (error) {
    return {};
  }
}

function isRoomRead(room, readRooms) {
  const readAt = readRooms[String(room?._id || "")];

  if (!readAt) return false;

  const readTime = Date.parse(readAt);
  const lastMessageTime = Date.parse(
    room?.lastMessageAt || room?.updatedAt || room?.createdAt || "",
  );

  if (!Number.isFinite(readTime)) return false;
  if (!Number.isFinite(lastMessageTime)) return true;

  return lastMessageTime <= readTime;
}
